import { useState } from "react";
import { Loader2, Plus, ShieldAlert, Trash2 } from "lucide-react";
import { toast } from "@/components/ui/sonner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { BAND_STYLES, CONTROL_EXAMPLES, LIKELIHOOD_SCALE, RISK_CATEGORIES, SEVERITY_SCALE } from "@/config/journeyCatalog";
import { PLAN_STATUS_LABELS, PLAN_STATUS_STYLES } from "@/config/journeyCatalog";

function newId() {
  return Math.random().toString(36).slice(2, 10);
}

export function RiskBadge({ score, band, testid }) {
  if (score === null || score === undefined) return <Badge variant="secondary" data-testid={testid}>Not rated</Badge>;
  return (
    <Badge className={BAND_STYLES[band] || ""} data-testid={testid}>
      {score} · {(band || "").toUpperCase()}
    </Badge>
  );
}

export default function RiskAssessmentPanel({ plan, reloadPlan }) {
  const { hasPermission } = useAuth();
  const [ra, setRa] = useState(null);
  const [flags, setFlags] = useState(null);
  const [loadingRa, setLoadingRa] = useState(null);
  const [hazards, setHazards] = useState([]);
  const [reviewDate, setReviewDate] = useState("");
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [missing, setMissing] = useState([]);

  const openRa = async (raId) => {
    setLoadingRa(raId);
    try {
      const { data } = await api.get(`/risk-assessments/${raId}`);
      setRa(data);
      setFlags(data.initial_assessment_flags);
      // normalise controls: must be an array of objects; guard against string/legacy shapes crashing .map
      const safeHazards = (data.hazards || []).map((h) => ({
        ...h,
        controls: Array.isArray(h.controls) ? h.controls : (h.controls ? [{ control: String(h.controls), responsible: "", status: "planned", comments: "" }] : []),
      }));
      setHazards(safeHazards);
      setReviewDate(data.review_date || "");
      setDirty(false);
      setMissing([]);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoadingRa(null);
    }
  };

  const createRa = async () => {
    try {
      const { data } = await api.post(`/journey-plans/${plan.id}/risk-assessments`);
      toast.success(`Risk assessment ${data.ra_number} created`);
      reloadPlan();
      openRa(data.id);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const editable = ra && ["draft", "returned_for_amendment"].includes(ra.status);

  const setHazard = (i, k, v) => {
    setHazards(hazards.map((h, j) => (j === i ? { ...h, [k]: v } : h)));
    setDirty(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.patch(`/risk-assessments/${ra.id}`, { hazards, review_date: reviewDate || null });
      setRa((r) => ({ ...r, hazards: data.hazards, review_date: data.review_date }));
      setHazards(data.hazards);
      setDirty(false);
      toast.success("Risk assessment saved");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const submit = async () => {
    await save();
    try {
      await api.post(`/risk-assessments/${ra.id}/submit`);
      toast.success("Risk assessment submitted for manager review");
      openRa(ra.id);
      reloadPlan();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (detail && typeof detail === "object" && detail.missing) {
        setMissing(detail.missing);
        toast.error(detail.message);
      } else {
        toast.error(formatApiError(err));
      }
    }
  };

  const review = async (decision, comments) => {
    try {
      await api.post(`/risk-assessments/${ra.id}/review`, { decision, comments: comments || null });
      toast.success("Review recorded");
      openRa(ra.id);
      reloadPlan();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const newVersion = async () => {
    const reason = window.prompt("Reason for new version (required, recorded in audit history):");
    if (!reason?.trim()) return;
    try {
      const { data } = await api.post(`/risk-assessments/${ra.id}/new-version`, { reason });
      toast.success(`Version ${data.version} created`);
      reloadPlan();
      openRa(data.id);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const canReview = (user) => user; // rendered conditionally by parent via permissions

  if (!ra) {
    return (
      <div className="space-y-3" data-testid="ra-list">
        {plan.risk_assessments?.length === 0 && (
          <p className="text-sm text-slate-600" data-testid="ra-empty">No risk assessment yet. The Individual Journey Risk Assessment is child-specific and journey-specific.</p>
        )}
        {(plan.risk_assessments || []).map((r) => (
          <Card key={r.id} data-testid={`ra-row-${r.id}`}>
            <CardContent className="p-4 flex items-center gap-3 flex-wrap">
              <button onClick={() => openRa(r.id)} data-testid={`ra-open-${r.id}`} className="font-medium text-primary hover:underline">
                {r.ra_number} — v{r.version}
              </button>
              <Badge className={PLAN_STATUS_STYLES[r.status]}>{PLAN_STATUS_LABELS[r.status]}</Badge>
              <span className="text-xs text-slate-500">{r.hazard_count} hazards</span>
              {loadingRa === r.id && <Loader2 className="h-4 w-4 animate-spin" />}
            </CardContent>
          </Card>
        ))}
        <Button data-testid="ra-create-button" onClick={createRa} className="h-10 bg-primary hover:bg-brand-hover text-white font-semibold">
          <Plus className="h-4 w-4 mr-2" /> New risk assessment
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="ra-editor">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2 flex-wrap">
          <Button variant="ghost" className="h-9 px-2" data-testid="ra-back-button" onClick={() => setRa(null)}>← All risk assessments</Button>
          <span className="font-heading font-bold text-slate-900" data-testid="ra-number">{ra.ra_number} — v{ra.version}</span>
          <Badge className={PLAN_STATUS_STYLES[ra.status]} data-testid="ra-status">{PLAN_STATUS_LABELS[ra.status]}</Badge>
        </div>
        <div className="flex flex-wrap gap-2">
          {editable && (
            <>
              <Button variant="outline" className="h-10" data-testid="ra-save-button" onClick={save} disabled={saving || !dirty}>
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save"}
              </Button>
              <Button className="h-10 bg-ukgreen hover:bg-green-800 text-white font-semibold" data-testid="ra-submit-button" onClick={submit}>
                Submit for review
              </Button>
            </>
          )}
          {["approved", "returned_for_amendment"].includes(ra.status) && (
            <Button variant="outline" className="h-10" data-testid="ra-new-version-button" onClick={newVersion}>New version</Button>
          )}
          <a href={`${process.env.REACT_APP_BACKEND_URL}/api/risk-assessments/${ra.id}/pdf`} target="_blank" rel="noreferrer"
            data-testid="ra-pdf-button"
            className="inline-flex h-10 items-center rounded-md border border-border px-3 text-sm font-medium text-primary hover:bg-muted">
            Risk assessment PDF
          </a>
        </div>
      </div>

      {ra.manager_review && (
        <div className="rounded border border-slate-200 bg-slate-50 px-3 py-2 text-sm" data-testid="ra-manager-review">
          <span className="font-semibold">Manager decision: {ra.manager_review.decision.replace(/_/g, " ")}</span>
          <span className="text-slate-600"> by {ra.manager_review.manager_name} · {(ra.manager_review.decided_at || "").slice(0, 16).replace("T", " ")}</span>
          {ra.manager_review.comments && <p className="text-xs text-slate-600 mt-1">{ra.manager_review.comments}</p>}
        </div>
      )}

      {ra.status === "awaiting_review" && hasPermission("journeys:review") && (
        <RaReviewPanel onReview={review} />
      )}

      {flags && (flags.road_safety_concern || (flags.safety_concerns || []).length > 0 || (flags.training_needs || []).length > 0) && (
        <div className="rounded-md border border-amber-300 bg-amber-50 p-4 space-y-2" data-testid="initial-assessment-flags">
          <p className="text-sm font-semibold text-amber-900 flex items-center gap-2">
            <ShieldAlert className="h-4 w-4" /> From the approved Initial Assessment ({flags.assessment_number}) — for your professional consideration
          </p>
          {flags.road_safety_concern && (
            <p className="text-sm text-amber-900"><strong>Road safety concern:</strong> {flags.road_safety_concern.description}</p>
          )}
          {(flags.safety_concerns || []).map((c, i) => (
            <p key={i} className="text-sm text-amber-900"><strong>{c.category}:</strong> {c.concern}</p>
          ))}
          {(flags.training_needs || []).map((n, i) => (
            <p key={i} className="text-sm text-amber-900"><strong>Training need:</strong> {n.area}{n.strategy ? ` — ${n.strategy}` : ""}</p>
          ))}
          <p className="text-xs text-amber-800">These are prompts only — decide whether and how each applies to the actual route.</p>
        </div>
      )}

      {missing.length > 0 && (
        <div className="rounded-md border border-red-300 bg-red-50 p-4" data-testid="ra-completeness-errors" role="alert">
          <p className="text-sm font-semibold text-red-800">Risk assessment cannot yet be submitted:</p>
          <ul className="mt-1 list-disc pl-5 text-sm text-red-800">{missing.map((m, i) => <li key={i}>{m}</li>)}</ul>
        </div>
      )}

      <div className="rounded-md border border-border bg-white p-3 text-xs text-slate-600" data-testid="risk-matrix-note">
        Risk matrix: Likelihood (1–5) × Severity (1–5) → score with band (LOW ≤ {ra.thresholds?.low_max ?? 6}, MEDIUM ≤ {ra.thresholds?.medium_max ?? 12}, HIGH above).
        <strong> Scores are decision-support only and never declare a journey safe or suitable — manager review is required.</strong>
      </div>

      <div className="space-y-3" data-testid="hazards-list">
        {hazards.map((h, i) => (
          <Card key={h.id || i} data-testid={`hazard-${i}`} className={h.residual_band === "high" ? "border-red-300" : ""}>
            <CardContent className="p-4 space-y-3">
              <div className="flex items-start justify-between gap-2 flex-wrap">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-semibold text-slate-900 text-sm">Hazard {i + 1}</span>
                  {h.residual_band === "high" && (
                    <Badge className="bg-red-600 text-white border-transparent" data-testid={`hazard-high-flag-${i}`}>MANAGER REVIEW REQUIRED</Badge>
                  )}
                </div>
                {editable && (
                  <Button type="button" variant="ghost" className="h-9 text-red-600" data-testid={`hazard-remove-${i}`}
                    onClick={() => { setHazards(hazards.filter((_, j) => j !== i)); setDirty(true); }}>
                    <Trash2 className="h-4 w-4" />
                  </Button>
                )}
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="space-y-1.5"><Label className="text-xs">Hazard category</Label>
                  <Select value={h.category || ""} disabled={!editable} onValueChange={(v) => setHazard(i, "category", v)}>
                    <SelectTrigger data-testid={`hazard-category-${i}`} className="h-10"><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent className="bg-white">
                      {RISK_CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c.replace(/_/g, " ")}</SelectItem>)}
                    </SelectContent>
                  </Select></div>
                <div className="space-y-1.5"><Label className="text-xs">Journey segment / location</Label>
                  <Input value={h.segment_ref || ""} disabled={!editable} data-testid={`hazard-segment-${i}`} onChange={(e) => setHazard(i, "segment_ref", e.target.value)} /></div>
              </div>
              <div className="space-y-1.5"><Label className="text-xs">Hazard description</Label>
                <Textarea rows={2} value={h.description || ""} disabled={!editable} data-testid={`hazard-description-${i}`} onChange={(e) => setHazard(i, "description", e.target.value)} /></div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="space-y-1.5"><Label className="text-xs">Who may be harmed</Label>
                  <Input value={h.who_harmed || ""} disabled={!editable} placeholder="Child" onChange={(e) => setHazard(i, "who_harmed", e.target.value)} /></div>
                <div className="space-y-1.5"><Label className="text-xs">Existing controls</Label>
                  <Input value={h.existing_controls || ""} disabled={!editable} onChange={(e) => setHazard(i, "existing_controls", e.target.value)} /></div>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 items-end">
                <div className="space-y-1.5"><Label className="text-xs">Likelihood</Label>
                  <Select value={h.likelihood ? String(h.likelihood) : ""} disabled={!editable} onValueChange={(v) => setHazard(i, "likelihood", parseInt(v, 10))}>
                    <SelectTrigger data-testid={`hazard-likelihood-${i}`} className="h-10"><SelectValue placeholder="L" /></SelectTrigger>
                    <SelectContent className="bg-white">{LIKELIHOOD_SCALE.map((s) => <SelectItem key={s.value} value={String(s.value)}>{s.label}</SelectItem>)}</SelectContent>
                  </Select></div>
                <div className="space-y-1.5"><Label className="text-xs">Severity</Label>
                  <Select value={h.severity ? String(h.severity) : ""} disabled={!editable} onValueChange={(v) => setHazard(i, "severity", parseInt(v, 10))}>
                    <SelectTrigger data-testid={`hazard-severity-${i}`} className="h-10"><SelectValue placeholder="S" /></SelectTrigger>
                    <SelectContent className="bg-white">{SEVERITY_SCALE.map((s) => <SelectItem key={s.value} value={String(s.value)}>{s.label}</SelectItem>)}</SelectContent>
                  </Select></div>
                <div><Label className="text-xs">Initial risk</Label><div className="pt-1.5"><RiskBadge score={h.risk_score} band={h.risk_band} testid={`hazard-initial-${i}`} /></div></div>
                <div><Label className="text-xs">Residual risk</Label><div className="pt-1.5"><RiskBadge score={h.residual_score} band={h.residual_band} testid={`hazard-residual-${i}`} /></div></div>
              </div>

              <div className="space-y-2">
                <Label className="text-xs font-semibold">Control measures</Label>
                {(h.controls || []).map((c, ci) => (
                  <div key={ci} className="grid grid-cols-1 sm:grid-cols-4 gap-2 rounded border border-border p-2" data-testid={`control-${i}-${ci}`}>
                    <Input placeholder="Control (e.g. repeated road-crossing practice)" value={c.control || ""} disabled={!editable}
                      data-testid={`control-text-${i}-${ci}`}
                      onChange={(e) => setHazard(i, "controls", h.controls.map((x, j) => (j === ci ? { ...x, control: e.target.value } : x)))} />
                    <Input placeholder="Responsible person" value={c.responsible || ""} disabled={!editable}
                      onChange={(e) => setHazard(i, "controls", h.controls.map((x, j) => (j === ci ? { ...x, responsible: e.target.value } : x)))} />
                    <Select value={c.status || "planned"} disabled={!editable}
                      onValueChange={(v) => setHazard(i, "controls", h.controls.map((x, j) => (j === ci ? { ...x, status: v } : x)))}>
                      <SelectTrigger className="h-10"><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-white">
                        <SelectItem value="planned">Planned</SelectItem>
                        <SelectItem value="in_place">In place</SelectItem>
                        <SelectItem value="review_required">Review required</SelectItem>
                      </SelectContent>
                    </Select>
                    {editable && (
                      <Button type="button" variant="ghost" className="h-9 text-red-600"
                        onClick={() => setHazard(i, "controls", h.controls.filter((_, j) => j !== ci))}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                ))}
                {editable && (
                  <Button type="button" variant="outline" className="h-9 text-xs" data-testid={`control-add-${i}`}
                    onClick={() => setHazard(i, "controls", [...(h.controls || []), { control: "", responsible: "", status: "planned", comments: "" }])}>
                    <Plus className="h-3.5 w-3.5 mr-1" /> Add control
                  </Button>
                )}
                {editable && (
                  <p className="text-[11px] text-slate-500">Examples: {CONTROL_EXAMPLES.slice(0, 4).join(" · ")}…</p>
                )}
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <div className="space-y-1.5"><Label className="text-xs">Residual likelihood</Label>
                  <Select value={h.residual_likelihood ? String(h.residual_likelihood) : ""} disabled={!editable} onValueChange={(v) => setHazard(i, "residual_likelihood", parseInt(v, 10))}>
                    <SelectTrigger data-testid={`hazard-res-likelihood-${i}`} className="h-10"><SelectValue placeholder="L" /></SelectTrigger>
                    <SelectContent className="bg-white">{LIKELIHOOD_SCALE.map((s) => <SelectItem key={s.value} value={String(s.value)}>{s.label}</SelectItem>)}</SelectContent>
                  </Select></div>
                <div className="space-y-1.5"><Label className="text-xs">Residual severity</Label>
                  <Select value={h.residual_severity ? String(h.residual_severity) : ""} disabled={!editable} onValueChange={(v) => setHazard(i, "residual_severity", parseInt(v, 10))}>
                    <SelectTrigger data-testid={`hazard-res-severity-${i}`} className="h-10"><SelectValue placeholder="S" /></SelectTrigger>
                    <SelectContent className="bg-white">{SEVERITY_SCALE.map((s) => <SelectItem key={s.value} value={String(s.value)}>{s.label}</SelectItem>)}</SelectContent>
                  </Select></div>
                <div className="space-y-1.5"><Label className="text-xs">Responsible person</Label>
                  <Input value={h.responsible_person || ""} disabled={!editable} onChange={(e) => setHazard(i, "responsible_person", e.target.value)} /></div>
                <div className="space-y-1.5"><Label className="text-xs">Target date</Label>
                  <Input type="date" value={h.target_date || ""} disabled={!editable} onChange={(e) => setHazard(i, "target_date", e.target.value)} /></div>
              </div>
              <div className="space-y-1.5"><Label className="text-xs">Assessor comments</Label>
                <Textarea rows={2} value={h.assessor_comments || ""} disabled={!editable} data-testid={`hazard-comments-${i}`} onChange={(e) => setHazard(i, "assessor_comments", e.target.value)} /></div>
            </CardContent>
          </Card>
        ))}
        {editable && (
          <Button type="button" variant="outline" className="h-10" data-testid="hazard-add-button"
            onClick={() => { setHazards([...hazards, { id: newId(), category: "", description: "", segment_ref: "", who_harmed: "Child", existing_controls: "", controls: [], responsible_person: "", target_date: "", assessor_comments: "" }]); setDirty(true); }}>
            <Plus className="h-4 w-4 mr-2" /> Add hazard
          </Button>
        )}
      </div>

      <div className="space-y-1.5 max-w-xs">
        <Label className="text-xs font-semibold">Risk assessment review date *</Label>
        <Input type="date" data-testid="ra-review-date" value={reviewDate} disabled={!editable}
          onChange={(e) => { setReviewDate(e.target.value); setDirty(true); }} />
        <p className="text-[11px] text-slate-500">Earlier review is triggered by incidents, route changes or changing needs.</p>
      </div>

      {ra.versions?.length > 1 && (
        <div className="flex flex-wrap gap-1.5" data-testid="ra-version-history">
          {ra.versions.map((v) => (
            <button key={v.id} onClick={() => openRa(v.id)} data-testid={`ra-version-${v.version}`}
              className={`rounded-full px-2.5 py-1 text-xs font-medium ${v.id === ra.id ? "bg-primary text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}>
              v{v.version} · {PLAN_STATUS_LABELS[v.status]}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// Manager review sub-panel rendered by parent with permissions
export function RaReviewPanel({ onReview }) {
  const [decision, setDecision] = useState("approve");
  const [comments, setComments] = useState("");
  return (
    <div className="rounded-md border-2 border-indigo-300 bg-indigo-50 p-4 space-y-3" data-testid="ra-review-panel">
      <h3 className="font-heading font-bold text-slate-900">Manager review — risk assessment</h3>
      <div className="space-y-1.5">
        <Label>Decision</Label>
        <Select value={decision} onValueChange={setDecision}>
          <SelectTrigger data-testid="ra-review-decision" className="h-11 bg-white"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-white">
            <SelectItem value="approve" data-testid="ra-review-approve">Approve</SelectItem>
            <SelectItem value="return_for_amendment" data-testid="ra-review-return">Return for amendment</SelectItem>
            <SelectItem value="request_information" data-testid="ra-review-info">Request additional information</SelectItem>
            <SelectItem value="require_reassessment" data-testid="ra-review-reassess">Require route reassessment</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div className="space-y-1.5">
        <Label>Comments {decision !== "approve" && "*"}</Label>
        <Textarea rows={2} value={comments} onChange={(e) => setComments(e.target.value)} data-testid="ra-review-comments" />
      </div>
      <Button onClick={() => onReview(decision, comments)} data-testid="ra-review-submit"
        className="h-11 bg-primary hover:bg-brand-hover text-white font-semibold">
        Record decision
      </Button>
    </div>
  );
}
